import {
  Id,
  Identifiable,
  Project,
  resource as resourceMock,
  list as listMock,
  task as taskMock,
  TimeSheet,
  TimeSheetFilter,
  Resource,
  project as projectMock,
  ResourceTypes,
  ProjectResource,
} from "@planzone/dto";
import { HttpClient, HttpMethods } from "../types/httpClient";
import { ProjectTimeSheet } from "../../dto/projectTimeSheet";

type ResponseList = {
  uuid: Id;
  name: string;
  nbrComments: number;
  spentTimes: { [key: string]: number };
  nbrAllocatedHours: number;
  totalOfSpentTime: number;
  totalOfSpentTimeInWeek: number;
  totalOfRemainingTime: number;
  percentComplete: number;
  subActivities: ResponseList[];
  tasks: {
    uuid: Id;
    name: string;
    spentTimes: { [key: string]: number };
    nbrAllocatedHours: number;
    totalOfSpentTime: number;
    totalOfSpentTimeInWeek: number;
    totalOfRemainingTime: number;
    timeRecords: {
      uuid: Id;
      date: number;
      month: number;
      year: number;
      timeSpent: number;
      attributes?: { [key: string]: string };
      timeSpentInWeek: number;
    }[];
    taskResourceLatestRecord: string;
    taskResourceLatestRecordingDay: string;
    resourcePercentComplete: number;
    allocationData: {
      allocationUuid: string;
      resourceUuid: string;
    }[];
  }[];
};

type Response = {
  projectUuid: string;
  projectName: string;
  integratedAt: string;
  projectSpentTimes: { [key: string]: number };
  totalSpentHours: number;
  totalAllocatedHours: number;
  totalAllocationsCount: number;
  timesheetDeadline: string;
  resources: {
    uuid: string;
    name: string;
    profession: null | string;
    type: ResourceTypes;
    deactivated: boolean;
    spentTimes: { [key: string]: number };
    unavailabilities: { [key: string]: boolean };
    totalOfAllocatedHours: number;
    totalOfSpentTime: number;
    totalOfSpentTimeInWeek: number;
    totalOfSpentTimeUntilEndDate: number;
    totalOfRemainingTime: number;
    percentCompletion: number;
    resourceRemoved?: boolean;
  }[];
};

function isUnAvailable(
  at: string,
  unAvailabilities: { [key: string]: boolean },
): boolean {
  return unAvailabilities[at];
}

function responseToSpentTimes(
  res: { [key: string]: number },
  unAvailabilities: { [key: string]: boolean },
): { date: string; time: number; isAvailable: boolean }[] {
  return Object.entries(res)
    .map(([date, time]) => ({
      date,
      time,
      isAvailable: !isUnAvailable(date, unAvailabilities),
    }))
    .sort((first, second) =>
      new Date(first.date) < new Date(second.date) ? -1 : 1,
    );
}

function formatList(
  list: ResponseList,
  project: Identifiable<Project>,
  resource: Identifiable<Resource> & {
    unavailabilities: { [key: string]: boolean };
  },
): TimeSheet["resources"][number]["lists"][number] {
  return {
    ...listMock,
    ...list,
    percentComplete: list.percentComplete,
    project: { ...projectMock, ...project },
    subLists: list.subActivities.map((subList) =>
      formatList(subList, project, resource),
    ),
    spentTimes: responseToSpentTimes(
      list.spentTimes,
      resource.unavailabilities,
    ),
    tasks: list.tasks.map((task) => ({
      ...taskMock,
      ...task,
      allocations: task.allocationData.map((allocation) => ({
        uuid: allocation.allocationUuid,
        allocatedHours: task.nbrAllocatedHours,
        percentComplete: task.resourcePercentComplete,
        estimatedAt: 0,
        completedAt: "",
        resource: { ...resource, uuid: allocation.resourceUuid },
      })),
      spentTimes: responseToSpentTimes(
        task.spentTimes,
        resource.unavailabilities,
      ),
      timeRecords: task.timeRecords.map((record) => ({
        ...record,
        task: { ...taskMock, ...task },
        resource,
      })),
    })),
  };
}

function responseToTimeSheet(res: Response): ProjectTimeSheet {
  return {
    ...res,
    spentTimes: responseToSpentTimes(res.projectSpentTimes, {}),
    timesheetDeadline: res.timesheetDeadline,
    project: {
      ...projectMock,
      ...res,
      uuid: res.projectUuid,
      name: res.projectName,
    },
    resources: res.resources.map((resource) => ({
      ...resourceMock,
      ...resource,
      profession: resource.profession || "",
      resourceRemoved: resource.resourceRemoved ?? false,
      spentTimes: responseToSpentTimes(
        resource.spentTimes,
        resource.unavailabilities,
      ),
    })),
  };
}

function getProjectTimeSheet(httpClient: HttpClient) {
  return (
    project: Identifiable<Project>,
    { minDate, maxDate, ...filter }: TimeSheetFilter,
    size: number,
    page: number,
  ) =>
    httpClient
      .sendHttpRequest<Response>({
        endpoint: "/timesheets/projects/paginated",
        method: HttpMethods.POST,
        data: {
          projectUuid: project.uuid,
          ...(filter.resources &&
            filter.resources.length > 0 && {
              resourceUuids: filter.resources.map(({ uuid }) => uuid),
            }),
          ...(filter.groups &&
            filter.groups.length > 0 && {
              resourceUuids: filter.groups.reduce<string[]>(
                (resources, group) => [
                  ...resources,
                  ...(group.resourceType === "project"
                    ? (group.resources as ProjectResource[]).map(
                        ({ resource }) => resource.uuid,
                      )
                    : group.resources.map(({ uuid }) => uuid)),
                ],
                [],
              ),
            }),
          includeDeactivated: filter.includeDeactivated,
          includeRemovedFromProject: filter.includeRemovedFromProject,
          startDate: minDate,
          endDate: maxDate,
          pageNumber: page,
          pageSize: size,
        },
      })
      .then((timesheet) => responseToTimeSheet(timesheet));
}

export { getProjectTimeSheet };
